import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-14 bg-[#FAF4F5]">
        <div className="w-full max-w-md bg-white border border-[#E8DCDD] rounded-sm p-8 text-center" data-testid="reset-password-missing">
          <ShieldAlert className="w-12 h-12 text-[#8C4A57] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#2D2628] mb-2">Missing reset token</h1>
          <p className="text-sm text-[#6B5E62] mb-6">Open the reset link from your email again, or request a new one.</p>
          <Link to="/forgot-password" className="btn-primary" data-testid="reset-password-request-new">Request a new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) { toast.error("Passwords don't match."); return; }
    if (pw1.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw1 });
      toast.success("Password updated — you can sign in now.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset your password — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-14 bg-[#FAF4F5]">
      <div className="w-full max-w-md bg-white border border-[#E8DCDD] rounded-sm p-8" data-testid="reset-password-page">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Password reset</div>
        <h1 className="font-serif text-3xl text-[#2D2628] mb-1">Choose a new password</h1>
        <p className="text-sm text-[#6B5E62] mb-6">Use at least 8 characters with a letter and a number.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">New password</label>
            <input
              type="password" required minLength={8} value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              data-testid="reset-password-new"
              className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Confirm password</label>
            <input
              type="password" required minLength={8} value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              data-testid="reset-password-confirm"
              className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"
            />
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="reset-password-submit"
            className="w-full bg-[#8C4A57] text-white text-[12px] tracking-[0.14em] uppercase font-semibold py-3 rounded-sm hover:bg-[#6f3945] transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Update password"}
          </button>
        </form>
        <p className="text-xs text-[#6B5E62] text-center mt-6">
          Changed your mind? <Link to="/login" className="text-[#8C4A57] font-semibold">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
